/**
 * 打ち合わせタブ
 *
 * Slack bot（ai-fassy の `@AI Fassy meet`）・Chrome拡張（meet-calendar）と同じ機構で
 * 打ち合わせ(Google Meet)を作成し、過去の打ち合わせ一覧（録画・文字起こし・議事録の
 * 有無を含む）と、1件を開いたときの文字起こし・議事録(Smart Notes)を表示する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件の打ち合わせ作成ボタン・過去一覧・詳細（文字起こし・議事録）を提供するタブ
 * @graph-connects useMeetings [calls] 打ち合わせ一覧取得
 * @graph-connects useCreateMeeting [calls] 打ち合わせ作成
 * @graph-connects useMeetingDetail [calls] 打ち合わせ詳細取得
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { Label } from '@cortex/web-design-system/components/ui/Label';
import {
  MultiPicker,
  type MultiPickerOption,
} from '@cortex/web-design-system/components/ui/MultiPicker';
import {
  useCreateMeeting,
  useMeetingDetail,
  useMeetings,
  useUpdateMeetingTags,
} from '../../../hooks/useMeetings.js';
import { ApiError, MEETING_TAGS } from '../../../lib/api.js';
import type { MeetingListItem, MeetingTag } from '../../../lib/api.js';

/** タグ選択 UI（絞り込み・編集の両方）共通の候補一覧。 @graph-connects none */
const TAG_OPTIONS: MultiPickerOption[] = MEETING_TAGS.map((tag) => ({ value: tag, label: tag }));

/**
 * 失敗できなかった理由を人が読む形にする。API が返したメッセージはそのまま見せる
 * （入口で訳し直すと、API 側で文言が変わったときに黙ってずれる。例えば
 * Slackチャンネル未所属による403は「この案件のSlackチャンネルに所属していないため
 * 打ち合わせを作成できません」という具体的な理由をAPIが返す）。
 * @graph-connects none
 */
function apiFailureMessage(t: TFunction, error: unknown, fallbackKey: string): string {
  if (error instanceof ApiError) return error.message;
  return t(fallbackKey);
}

/** ISO8601 を `YYYY-MM-DD HH:MM` に整形する（時刻まで見たいのは同日複数回の打ち合わせを区別するため）。 @graph-connects none */
function formatDateTime(iso: string): string {
  return iso.replace('T', ' ').slice(0, 16);
}

/**
 * 成果物の有無を示す小さな丸バッジ。元ファイル（Google Drive）のURLがあればリンクにする
 * （録画・文字起こし・議事録そのものへ遷移。行クリック＝詳細モーダルに巻き込まれないよう
 * クリックの伝播を止める）。URLが無い場合はただの状態表示のまま。
 * @graph-connects none
 */
function ReadyDot({ ready, label, url }: { ready: boolean; label: string; url: string | null }) {
  const content = (
    <>
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: ready ? 'currentColor' : 'var(--color-border)' }}
      />
      {label}
    </>
  );
  const className = `inline-flex items-center gap-1 text-xs ${ready ? 'text-[var(--color-success)]' : 'text-[var(--color-text-muted)]'}`;

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${className} hover:underline`}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {content}
      </a>
    );
  }
  return <span className={className}>{content}</span>;
}

/**
 * 打ち合わせ作成ボタン・目的入力ポップアップ・作成直後の参加導線。
 * @graph-connects useCreateMeeting [calls] 打ち合わせ作成
 */
function CreateMeetingAction({ pjKey }: { pjKey: string }) {
  const { t } = useTranslation();
  const createMeeting = useCreateMeeting(pjKey);
  const [open, setOpen] = useState(false);
  const [purpose, setPurpose] = useState('');

  const confirm = () => {
    const trimmed = purpose.trim();
    createMeeting.mutate(
      { purpose: trimmed === '' ? undefined : trimmed },
      {
        onSuccess: () => {
          setOpen(false);
          setPurpose('');
        },
      },
    );
  };

  /** モーダルを閉じる（キャンセル・背景クリック共通）。失敗後のエラー表示がモーダル外に
   * 残り続けないよう、mutation のエラー状態もあわせてクリアする。
   * @graph-connects none
   */
  const closeModal = () => {
    setOpen(false);
    createMeeting.reset();
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button
        variant="primary"
        onClick={() => {
          setOpen(true);
        }}
      >
        {t('meetings.createButton')}
      </Button>
      {createMeeting.isSuccess ? (
        <a
          href={createMeeting.data.meetingUri}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-[var(--color-accent-primary)] hover:underline"
        >
          {t('meetings.joinButton')}
        </a>
      ) : null}
      {createMeeting.isError && !open ? (
        <span className="text-sm text-[var(--color-error)]">
          {apiFailureMessage(t, createMeeting.error, 'meetings.createFailed')}
        </span>
      ) : null}

      <Modal
        open={open}
        onClose={closeModal}
        title={t('meetings.createModal.title')}
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-2">
            {createMeeting.isError ? (
              <span className="mr-auto text-xs text-[var(--color-error)]">
                {apiFailureMessage(t, createMeeting.error, 'meetings.createFailed')}
              </span>
            ) : null}
            <Button type="button" variant="ghost" onClick={closeModal}>
              {t('meetings.createModal.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={createMeeting.isPending}
              onClick={confirm}
            >
              {t('meetings.createModal.confirm')}
            </Button>
          </div>
        }
      >
        <div className="space-y-2">
          <Label htmlFor="meeting-purpose">{t('meetings.createModal.purposeLabel')}</Label>
          <Textarea
            id="meeting-purpose"
            rows={3}
            value={purpose}
            onChange={(e) => {
              setPurpose(e.target.value);
            }}
            placeholder={t('meetings.createModal.purposePlaceholder')}
          />
        </div>
      </Modal>
    </div>
  );
}

/**
 * 打ち合わせ詳細モーダル（文字起こし・議事録）。
 * @graph-connects useMeetingDetail [calls] 打ち合わせ詳細取得
 */
function MeetingDetailModal({
  pjKey,
  meetingId,
  onClose,
}: {
  pjKey: string;
  meetingId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useMeetingDetail(pjKey, meetingId);

  return (
    <Modal
      open={meetingId !== null}
      onClose={onClose}
      title={data ? formatDateTime(data.createdAt) : (meetingId ?? '')}
      size="full"
    >
      {isLoading || !data ? (
        error ? (
          <p className="text-sm text-[var(--color-text-secondary)]">{t('meetings.loadFailed')}</p>
        ) : (
          <div className="flex items-center justify-center py-8">
            <div className="loading-spinner" />
          </div>
        )
      ) : (
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-2">
              {t('meetings.detail.notesHeading')}
            </h3>
            {data.notesAvailable && data.notesText ? (
              <p className="text-base leading-relaxed text-[var(--color-text-secondary)] whitespace-pre-wrap max-h-[45vh] overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4">
                {data.notesText}
              </p>
            ) : (
              <p className="text-sm text-[var(--color-text-muted)]">
                {t('meetings.detail.notesUnavailable')}
              </p>
            )}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-2">
              {t('meetings.detail.transcriptHeading')}
            </h3>
            {data.transcriptText ? (
              <p className="text-base leading-relaxed text-[var(--color-text-secondary)] whitespace-pre-wrap max-h-[45vh] overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4">
                {data.transcriptText}
              </p>
            ) : (
              <p className="text-sm text-[var(--color-text-muted)]">
                {t('meetings.detail.transcriptUnavailable')}
              </p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * 打ち合わせの種類タグ編集。表示（閉じた状態のチップ列）と手動編集を兼ねる。
 * 行クリック（詳細モーダルを開く）に巻き込まれないよう、クリックの伝播を止める。
 * @graph-connects useUpdateMeetingTags [calls] タグ上書き
 */
function TagEditor({
  pjKey,
  meetingId,
  tags,
}: {
  pjKey: string;
  meetingId: string;
  tags: MeetingTag[];
}) {
  const { t } = useTranslation();
  const updateTags = useUpdateMeetingTags(pjKey);

  return (
    <div
      className="min-w-[180px]"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <MultiPicker
        aria-label={t('meetings.tagEditor.ariaLabel')}
        options={TAG_OPTIONS}
        value={tags}
        onChange={(next) => {
          updateTags.mutate({ meetingId, tags: next as MeetingTag[] });
        }}
        placeholder={t('meetings.tagEditor.placeholder')}
        searchPlaceholder={t('meetings.tagEditor.searchPlaceholder')}
        emptyMessage={t('meetings.tagEditor.emptyMessage')}
      />
      {updateTags.isError ? (
        <p className="text-xs text-[var(--color-error)] mt-1">
          {apiFailureMessage(t, updateTags.error, 'meetings.tagEditor.updateFailed')}
        </p>
      ) : null}
    </div>
  );
}

/** 打ち合わせ一覧テーブル。 @graph-connects useUpdateMeetingTags [calls] タグ上書き */
function MeetingsList({
  pjKey,
  meetings,
  onSelect,
}: {
  pjKey: string;
  meetings: MeetingListItem[];
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto border border-[var(--color-border)] rounded-lg">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-[var(--color-bg-hover)]">
            <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] px-3 py-2 border-b border-[var(--color-border)] whitespace-nowrap">
              {t('meetings.columns.createdAt')}
            </th>
            <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] px-3 py-2 border-b border-[var(--color-border)] whitespace-nowrap">
              {t('meetings.columns.calendarEventTitle')}
            </th>
            <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] px-3 py-2 border-b border-[var(--color-border)] whitespace-nowrap">
              {t('meetings.columns.purpose')}
            </th>
            <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] px-3 py-2 border-b border-[var(--color-border)] whitespace-nowrap">
              {t('meetings.columns.participants')}
            </th>
            <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] px-3 py-2 border-b border-[var(--color-border)] whitespace-nowrap">
              {t('meetings.columns.tags')}
            </th>
            <th className="text-left text-xs font-semibold text-[var(--color-text-secondary)] px-3 py-2 border-b border-[var(--color-border)] whitespace-nowrap">
              {t('meetings.columns.artifacts')}
            </th>
          </tr>
        </thead>
        <tbody>
          {meetings.map((m, index) => (
            <tr
              key={m.id}
              className={`cursor-pointer hover:bg-[var(--color-bg-hover)] ${index % 2 === 1 ? 'bg-[var(--color-bg-elevated)]' : 'bg-[var(--color-bg-surface)]'}`}
              onClick={() => {
                onSelect(m.id);
              }}
            >
              <td className="px-3 py-2 border-t border-[var(--color-border)] whitespace-nowrap text-[var(--color-text-secondary)]">
                {formatDateTime(m.createdAt)}
              </td>
              <td className="px-3 py-2 border-t border-[var(--color-border)]">
                {m.calendarEventTitle ?? '-'}
              </td>
              <td className="px-3 py-2 border-t border-[var(--color-border)]">
                {m.purpose ?? '-'}
              </td>
              <td className="px-3 py-2 border-t border-[var(--color-border)]">
                {m.attendeeEmails.length > 0 ? (
                  <span
                    className="block max-w-[220px] truncate"
                    title={m.attendeeEmails.join(', ')}
                  >
                    {m.attendeeEmails.join(', ')}
                  </span>
                ) : (
                  '-'
                )}
              </td>
              <td className="px-3 py-2 border-t border-[var(--color-border)]">
                <TagEditor pjKey={pjKey} meetingId={m.id} tags={m.tags} />
              </td>
              <td className="px-3 py-2 border-t border-[var(--color-border)] whitespace-nowrap">
                <div className="flex gap-3">
                  <ReadyDot
                    ready={m.recordingReady}
                    label={t('meetings.columns.recording')}
                    url={m.recordingUrl}
                  />
                  <ReadyDot
                    ready={m.transcriptReady}
                    label={t('meetings.columns.transcript')}
                    url={m.transcriptUrl}
                  />
                  <ReadyDot
                    ready={m.notesReady}
                    label={t('meetings.columns.notes')}
                    url={m.notesUrl}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * 打ち合わせタブ本体。
 * @graph-connects useMeetings [calls] 打ち合わせ一覧取得（タグ絞り込み対応）
 */
export function MeetingsTab({ pjKey }: { pjKey: string }) {
  const { t } = useTranslation();
  const [tagFilter, setTagFilter] = useState<MeetingTag[]>([]);
  const list = useMeetings(pjKey, tagFilter);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const meetings = list.data?.meetings ?? [];

  return (
    <div className="space-y-6 pt-4">
      <CreateMeetingAction pjKey={pjKey} />

      <div>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <h2 className="text-base font-semibold">{t('meetings.listHeading')}</h2>
          <div className="w-full max-w-xs">
            <MultiPicker
              aria-label={t('meetings.tagFilter.ariaLabel')}
              options={TAG_OPTIONS}
              value={tagFilter}
              onChange={(next) => {
                setTagFilter(next as MeetingTag[]);
              }}
              placeholder={t('meetings.tagFilter.placeholder')}
              searchPlaceholder={t('meetings.tagEditor.searchPlaceholder')}
              emptyMessage={t('meetings.tagEditor.emptyMessage')}
            />
          </div>
        </div>
        {list.error ? (
          <p className="text-sm text-[var(--color-text-secondary)]">{t('meetings.loadFailed')}</p>
        ) : list.data === undefined ? (
          <p className="text-sm text-[var(--color-text-muted)]">{t('meetings.loading')}</p>
        ) : meetings.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">
            {tagFilter.length > 0 ? t('meetings.emptyFiltered') : t('meetings.empty')}
          </p>
        ) : (
          <MeetingsList
            pjKey={pjKey}
            meetings={meetings}
            onSelect={(id) => {
              setSelectedId(id);
            }}
          />
        )}
      </div>

      <MeetingDetailModal
        pjKey={pjKey}
        meetingId={selectedId}
        onClose={() => {
          setSelectedId(null);
        }}
      />
    </div>
  );
}
